import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Info, Sparkle, TrendUp, TrashSimple } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

const emptyRules = {
  enabled: false,
  max_money_commit: 0,
  forbidden_topics: [],
  forbidden_phrases: [],
  require_business_hours: false,
  max_confidence_needed: 0.75,
  block_first_message_in_thread: true,
};

function SelfTunePanel({ analysis, onApplied }) {
  const [busyKey, setBusyKey] = useState(null);
  if (!analysis) return null;

  const apply = async (key) => {
    setBusyKey(key);
    try {
      await api.post("/relay/rules/overrides/apply", { apply_key: key });
      toast.success("Rule softened. Similar drafts will now auto-send.");
      await onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setBusyKey(null); }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all override history? Suggestions will disappear.")) return;
    await api.delete("/relay/rules/overrides");
    toast.success("Override history cleared");
    await onApplied();
  };

  const fmt = (v) => {
    if (Array.isArray(v)) return v.length ? v.join(", ") : "(empty)";
    if (typeof v === "boolean") return v ? "on" : "off";
    if (typeof v === "number") return v.toString();
    return String(v);
  };

  return (
    <div className="oz-card p-5 mb-6" data-testid="self-tune-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendUp size={16} color="var(--oz-gold)" />
          <div className="overline">Self-Tuning · Learn from your overrides</div>
        </div>
        {analysis.total_overrides > 0 && (
          <button onClick={clearAll} className="btn-ghost text-xs flex items-center gap-1" data-testid="clear-overrides-btn">
            <TrashSimple size={12} /> Clear history
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <div className="overline mb-1">Total overrides</div>
          <div className="font-mono text-2xl">{analysis.total_overrides}</div>
        </div>
        <div>
          <div className="overline mb-1">Clean overrides</div>
          <div className="font-mono text-2xl text-[color:var(--oz-gold)]">{analysis.clean_overrides}</div>
          <div className="text-[10px] text-[color:var(--oz-text-muted)] font-mono">sent unchanged</div>
        </div>
        <div>
          <div className="overline mb-1">Suggestions ready</div>
          <div className="font-mono text-2xl text-emerald-400">{analysis.suggestions?.length || 0}</div>
        </div>
      </div>

      {analysis.suggestions?.length === 0 && (
        <div className="text-xs text-[color:var(--oz-text-muted)] italic border-t border-[color:var(--oz-border)] pt-3">
          {analysis.total_overrides === 0
            ? "No overrides yet. Every time you hit \"Send as Mark\" on a held draft without editing it, we treat it as a silent \"too strict\" signal. After a few, we'll suggest specific rule loosenings here."
            : "Not enough overrides on any single rule yet to suggest a change. Keep going — rules soften once a threshold is crossed."}
        </div>
      )}

      <div className="space-y-3">
        {(analysis.suggestions || []).map((s) => (
          <div key={s.apply_key} className="border border-[color:var(--oz-border)] bg-[color:var(--oz-page)] rounded-sm p-4" data-testid={`suggestion-${s.apply_key}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkle size={12} weight="fill" color="var(--oz-gold)" />
                  <div className="text-sm font-medium">{s.label}</div>
                  <span className="chip chip-yellow font-mono text-[10px]">×{s.override_count}</span>
                </div>
                <div className="text-xs text-[color:var(--oz-text-dim)] mb-2">{s.reason}</div>
                <div className="flex items-center gap-2 text-xs font-mono">
                  <span className="bg-red-500/10 text-red-300 px-2 py-0.5 rounded-sm line-through">{fmt(s.current_value)}</span>
                  <span className="text-[color:var(--oz-text-muted)]">→</span>
                  <span className="bg-emerald-500/15 text-emerald-300 px-2 py-0.5 rounded-sm">{fmt(s.proposed_value)}</span>
                </div>
              </div>
              <button
                onClick={() => apply(s.apply_key)}
                disabled={busyKey === s.apply_key}
                className="btn-primary text-xs shrink-0"
                data-testid={`apply-${s.apply_key}`}
              >
                {busyKey === s.apply_key ? "Applying…" : "Apply"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Rules() {
  const [rules, setRules] = useState(emptyRules);
  const [topicsInput, setTopicsInput] = useState("");
  const [phrasesInput, setPhrasesInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.get("/relay/rules"),
        api.get("/relay/rules/overrides/analysis"),
      ]);
      setRules(r.data);
      setTopicsInput((r.data.forbidden_topics || []).join(", "));
      setPhrasesInput((r.data.forbidden_phrases || []).join(", "));
      setAnalysis(a.data);
    } catch { toast.error("Failed to load rules"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!rules.enabled,
        max_money_commit: +rules.max_money_commit || 0,
        forbidden_topics: topicsInput.split(",").map((s) => s.trim()).filter(Boolean),
        forbidden_phrases: phrasesInput.split(",").map((s) => s.trim()).filter(Boolean),
        require_business_hours: !!rules.require_business_hours,
        max_confidence_needed: +rules.max_confidence_needed || 0.75,
        block_first_message_in_thread: !!rules.block_first_message_in_thread,
      };
      const { data } = await api.put("/relay/rules", payload);
      setRules(data);
      toast.success("Rules saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-[color:var(--oz-text-dim)]">Loading rules…</div>;

  const ToggleRow = ({ label, description, value, onChange, testId }) => (
    <div className="flex items-center justify-between py-3 border-b border-[color:var(--oz-border)] last:border-0">
      <div className="flex-1 pr-4">
        <div className="text-sm text-[color:var(--oz-text)]">{label}</div>
        {description && <div className="text-[11px] text-[color:var(--oz-text-muted)] mt-0.5">{description}</div>}
      </div>
      <button
        data-testid={testId}
        onClick={() => onChange(!value)}
        className={`w-11 h-6 rounded-full border transition-colors relative ${value ? "bg-[color:var(--oz-emerald)] border-[color:var(--oz-emerald)]" : "bg-transparent border-[color:var(--oz-border-strong)]"}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${value ? "left-6" : "left-0.5"}`}></span>
      </button>
    </div>
  );

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <PageHeader
        overline='Decision Tree · "Can the Agent Act?"'
        title="Auto-Approve Rules"
        subtitle="Set the thresholds. If a draft passes every rule, the AI sends it as Mark automatically. If any rule fails, it holds for approval."
      />

      <div className={`oz-card p-5 mb-6 border-l-4 ${rules.enabled ? "border-l-[color:var(--oz-emerald)]" : "border-l-[color:var(--oz-amber)]"}`}>
        <div className="flex items-center gap-3">
          <ShieldCheck size={24} weight="fill" color={rules.enabled ? "var(--oz-emerald)" : "var(--oz-amber)"} />
          <div className="flex-1">
            <div className="font-display text-lg">Auto-approve is {rules.enabled ? "ACTIVE" : "OFF"}</div>
            <div className="text-xs text-[color:var(--oz-text-dim)]">
              {rules.enabled
                ? "Drafts that pass every rule below will be sent automatically. Others hold for your approval."
                : "Every AI-drafted reply waits for your manual approve/edit/send."}
            </div>
          </div>
          <button
            data-testid="toggle-enabled"
            onClick={() => setRules({ ...rules, enabled: !rules.enabled })}
            className={rules.enabled ? "btn-ghost text-sm" : "btn-primary text-sm"}
          >
            {rules.enabled ? "Disable" : "Enable"}
          </button>
        </div>
      </div>

      <SelfTunePanel analysis={analysis} onApplied={load} />

      <div className="oz-card p-5 mb-6">
        <div className="overline mb-3">Money limit</div>
        <div className="flex items-center gap-3">
          <div className="text-2xl font-mono text-[color:var(--oz-gold)]">$</div>
          <input
            data-testid="money-limit"
            type="number"
            min="0"
            value={rules.max_money_commit}
            onChange={(e) => setRules({ ...rules, max_money_commit: e.target.value })}
            className="flex-1 bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm font-mono"
          />
        </div>
        <div className="text-[11px] text-[color:var(--oz-text-muted)] mt-2 flex gap-1 items-start">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>If a draft commits Mark to more than this amount, it will always hold for approval. Set to 0 to never auto-send anything that commits money.</span>
        </div>
      </div>

      <div className="oz-card p-5 mb-6">
        <div className="overline mb-3">Confidence threshold</div>
        <div className="flex items-center gap-4">
          <input
            data-testid="confidence"
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={rules.max_confidence_needed}
            onChange={(e) => setRules({ ...rules, max_confidence_needed: +e.target.value })}
            className="flex-1"
          />
          <div className="font-mono text-lg text-[color:var(--oz-emerald)] w-16 text-right">{Math.round(rules.max_confidence_needed * 100)}%</div>
        </div>
        <div className="text-[11px] text-[color:var(--oz-text-muted)] mt-2">
          After drafting, Claude self-scores how confident it is that you'd approve the message. Below this threshold → hold for approval.
        </div>
      </div>

      <div className="oz-card p-5 mb-6">
        <div className="overline mb-3">Forbidden topics</div>
        <input
          data-testid="topics"
          value={topicsInput}
          onChange={(e) => setTopicsInput(e.target.value)}
          placeholder="legal, press, resignation"
          className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm"
        />
        <div className="text-[11px] text-[color:var(--oz-text-muted)] mt-2">Comma-separated. If the draft touches any of these, hold it. </div>
      </div>

      <div className="oz-card p-5 mb-6">
        <div className="overline mb-3">Forbidden phrases</div>
        <input
          data-testid="phrases"
          value={phrasesInput}
          onChange={(e) => setPhrasesInput(e.target.value)}
          placeholder='I promise, I guarantee, "signed & sealed"'
          className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm"
        />
        <div className="text-[11px] text-[color:var(--oz-text-muted)] mt-2">Comma-separated substring matches, case-insensitive.</div>
      </div>

      <div className="oz-card p-5 mb-6">
        <ToggleRow
          label="Block the first outbound of any new thread"
          description="Never auto-send the first message to a new client. You always eyeball it."
          value={rules.block_first_message_in_thread}
          onChange={(v) => setRules({ ...rules, block_first_message_in_thread: v })}
          testId="toggle-first-block"
        />
        <ToggleRow
          label="Only auto-send during business hours"
          description="9am–6pm ET. Outside that window, drafts always hold."
          value={rules.require_business_hours}
          onChange={(v) => setRules({ ...rules, require_business_hours: v })}
          testId="toggle-hours"
        />
      </div>

      <div className="flex gap-2 justify-end">
        <button onClick={load} className="btn-ghost text-sm">Reset</button>
        <button data-testid="save-rules" onClick={save} disabled={saving} className="btn-primary text-sm">
          {saving ? "Saving…" : "Save rules"}
        </button>
      </div>
    </div>
  );
}
